import { useCallback, useLayoutEffect, useRef } from 'react';

export const prefersReducedMotion = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Run `fn` on the next animation frame; returns a canceller. */
export const nextFrame = (fn) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    const id = window.requestAnimationFrame(fn);
    return () => window.cancelAnimationFrame(id);
  }
  const id = setTimeout(fn, 16);
  return () => clearTimeout(id);
};

// A capture that no render followed within this time is stale: playing it
// later would animate cards from where they were before an unrelated update.
const CAPTURE_TTL_MS = 250;

const flipNodes = (container) => Array.from(container.children).filter((node) => node.dataset && node.dataset.flipId);

/**
 * FLIP animation for the cards of one grid. Call the returned `capture()`
 * right before a state change that moves cards. After React commits, every
 * card that moved starts from where it was on screen and slides to its new
 * place on the layout spring (--motion-layout). Only transforms animate, so
 * reflowing 30 cards stays cheap. A card with .is-resizing follows the
 * pointer itself and is left alone. With reduced motion nothing animates.
 */
export default function useFlip(containerRef) {
  const pendingRef = useRef(null);
  const generationRef = useRef(0);

  const capture = useCallback(() => {
    const container = containerRef.current;
    if (!container || prefersReducedMotion()) return;
    const rects = new Map();
    flipNodes(container).forEach((node) => rects.set(node.dataset.flipId, node.getBoundingClientRect()));
    pendingRef.current = { rects, at: Date.now() };
  }, [containerRef]);

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    const container = containerRef.current;
    if (!pending || !container) return;
    pendingRef.current = null;
    if (Date.now() - pending.at > CAPTURE_TTL_MS) return;

    generationRef.current += 1;
    const generation = generationRef.current;
    const nodes = flipNodes(container).filter((node) => !node.classList.contains('is-resizing'));
    nodes.forEach((node) => {
      node.style.transition = 'none';
      node.style.transform = '';
    });
    const moved = [];
    nodes.forEach((node) => {
      const before = pending.rects.get(node.dataset.flipId);
      if (!before) return;
      const after = node.getBoundingClientRect();
      const dx = before.left - after.left;
      const dy = before.top - after.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      moved.push(node);
    });
    nodes.forEach((node) => { if (!moved.includes(node)) node.style.transition = ''; });
    if (!moved.length) return;

    // Lay out the inverted positions, then release them on the next frame.
    container.getBoundingClientRect();
    nextFrame(() => {
      if (generation !== generationRef.current) return;
      moved.forEach((node) => {
        node.style.transition = 'transform var(--motion-layout)';
        node.style.transform = '';
      });
    });
    setTimeout(() => {
      if (generation !== generationRef.current) return;
      moved.forEach((node) => { node.style.transition = ''; });
    }, 400);
  });

  return capture;
}
